import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArtifactLink } from '../components/ArtifactLink';
import { ARTIFACT_TYPE_LABELS, type SubmissionView } from '@thottbot/shared';
import { apiGet, apiSend, useMe } from '../api/client';
import { SubmissionDiff } from '../features/mod/SubmissionDiff';
import { relativeTime } from '../util/time';
import { describeError } from './Submit';
import { NotFound } from './NotFound';

interface Queue {
  page: number;
  total: number;
  items: SubmissionView[];
}
export interface ReportItem {
  id: string;
  targetType: 'comment' | 'screenshot';
  targetId: string;
  reason: string;
  reporterBattletag: string;
  createdAt: string;
  target: Record<string, unknown> | null;
}

export function ModQueue() {
  const me = useMe();
  if (me.isPending) return <div className="page muted">Loading...</div>;
  const user = me.data?.user ?? null;
  if (!user || user.role === 'player') return <Forbidden />;
  return (
    <div className="page">
      <title>Moderation queue</title>
      <h1>Moderation queue</h1>
      <PendingSubmissions />
      <OpenReports base="/api/mod" />
    </div>
  );
}

export function Forbidden() {
  return <NotFound message="You need a moderator or admin account for this page." />;
}

function PendingSubmissions() {
  const qc = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);
  const queue = useQuery({
    queryKey: ['mod', 'queue'],
    queryFn: () => apiGet<Queue>('/api/mod/queue?status=pending'),
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['mod', 'queue'] });
    qc.invalidateQueries({ queryKey: ['stats'] });
  };
  return (
    <section className="section" data-testid="queue">
      <h2>
        Pending submissions {queue.data && <span className="muted">({queue.data.total})</span>}
      </h2>
      {toast && (
        <div className="notice ok" role="status">
          {toast}
        </div>
      )}
      {queue.isPending && <p className="muted">Loading...</p>}
      {queue.data?.items.length === 0 && <p className="muted">The queue is empty.</p>}
      {queue.data?.items.map((s) => (
        <SubmissionCard
          key={s.id}
          submission={s}
          onDone={(msg) => {
            setToast(msg);
            refresh();
          }}
        />
      ))}
    </section>
  );
}

function SubmissionCard({
  submission,
  onDone,
}: {
  submission: SubmissionView;
  onDone: (msg: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const approve = useMutation({
    mutationFn: () => apiSend('POST', `/api/mod/submissions/${submission.id}/approve`),
    onSuccess: () => onDone('Approved'),
  });
  const reject = useMutation({
    mutationFn: () => apiSend('POST', `/api/mod/submissions/${submission.id}/reject`, { reason }),
    onSuccess: () => onDone('Rejected'),
  });
  const error = approve.error ?? reject.error;
  const typeLabel = ARTIFACT_TYPE_LABELS[submission.type].singular;
  return (
    <div className="notice" data-testid="submission" style={{ background: '#fff' }}>
      <p>
        <b>{submission.kind === 'create' ? 'New' : 'Edit'}</b> {typeLabel}:{' '}
        {submission.kind === 'edit' && submission.current ? (
          <ArtifactLink type={submission.type} slug={submission.current.slug} name={submission.current.name ?? submission.proposedName}>
            {submission.proposedName}
          </ArtifactLink>
        ) : (
          <b>{submission.proposedName}</b>
        )}{' '}
        <span className="muted">
          by {submission.authorBattletag} · {relativeTime(submission.createdAt)}
          {submission.verifiedInForever && ' · verified in Forever'}
        </span>
      </p>
      {submission.note && (
        <p>
          <i>Note:</i> {submission.note}
        </p>
      )}
      <SubmissionDiff submission={submission} />
      {error && <div className="error">{describeError(error)}</div>}
      <div className="actions">
        <button
          type="button"
          onClick={() => approve.mutate()}
          disabled={approve.isPending || reject.isPending}
        >
          Approve
        </button>
        {rejecting ? (
          <>
            <input
              type="text"
              aria-label="Rejection reason"
              placeholder="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
            />
            <button
              type="button"
              onClick={() => reject.mutate()}
              disabled={!reason.trim() || reject.isPending}
            >
              Confirm reject
            </button>
            <button type="button" onClick={() => setRejecting(false)}>
              Cancel
            </button>
          </>
        ) : (
          <button type="button" onClick={() => setRejecting(true)}>
            Reject
          </button>
        )}
      </div>
    </div>
  );
}

/** Open reports with remove/resolve actions; `base` is /api/mod or /api/admin. */
export function OpenReports({ base }: { base: '/api/mod' | '/api/admin' }) {
  const qc = useQueryClient();
  const reports = useQuery({
    queryKey: ['reports', base],
    queryFn: () => apiGet<{ items: ReportItem[] }>(`${base}/reports?status=open`),
  });
  const [error, setError] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['reports', base] });
  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      refresh();
    } catch (err) {
      setError(describeError(err));
    }
  };
  return (
    <section className="section" data-testid="reports">
      <h2>
        Open reports {reports.data && <span className="muted">({reports.data.items.length})</span>}
      </h2>
      {error && <div className="error">{error}</div>}
      {reports.data?.items.length === 0 && <p className="muted">No open reports.</p>}
      {reports.data && reports.data.items.length > 0 && (
        <div className="table-scroll">
          <table className="grid">
            <thead>
              <tr>
                <th>Target</th>
                <th>Reason</th>
                <th>Reported by</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.data.items.map((r) => (
                <tr key={r.id} data-testid="report">
                  <td>
                    {r.targetType === 'comment' ? (
                      <span>
                        Comment by {String(r.target?.authorBattletag ?? '?')}:{' '}
                        <i>{String(r.target?.body ?? '(removed)').slice(0, 200)}</i>
                      </span>
                    ) : (
                      <span>
                        Screenshot by {String(r.target?.uploaderBattletag ?? '?')}{' '}
                        {typeof r.target?.url === 'string' && (
                          <a href={r.target.url} target="_blank" rel="noreferrer">
                            view
                          </a>
                        )}
                      </span>
                    )}
                    {r.target?.removed === true && (
                      <span className="muted"> (already removed)</span>
                    )}
                  </td>
                  <td>{r.reason}</td>
                  <td>
                    {r.reporterBattletag} <span className="muted">{relativeTime(r.createdAt)}</span>
                  </td>
                  <td className="actions">
                    <ConfirmButton
                      label={`Remove ${r.targetType}`}
                      onConfirm={() =>
                        act(() => apiSend('DELETE', `${base}/${r.targetType}s/${r.targetId}`))
                      }
                    />
                    <button
                      type="button"
                      onClick={() => act(() => apiSend('POST', `${base}/reports/${r.id}/resolve`))}
                    >
                      Dismiss
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Two-click confirmation rendered inline (no browser dialogs). */
export function ConfirmButton({
  label,
  onConfirm,
  danger = true,
}: {
  label: string;
  onConfirm: () => void | Promise<unknown>;
  danger?: boolean;
}) {
  const [armed, setArmed] = useState(false);
  if (!armed) {
    return (
      <button
        type="button"
        onClick={() => setArmed(true)}
        style={danger ? { color: 'var(--danger)' } : undefined}
      >
        {label}
      </button>
    );
  }
  return (
    <span className="actions" style={{ display: 'inline-flex' }}>
      <button
        type="button"
        onClick={async () => {
          await onConfirm();
          setArmed(false);
        }}
        style={{ color: 'var(--danger)', fontWeight: 'bold' }}
      >
        Confirm {label.toLowerCase()}
      </button>
      <button type="button" onClick={() => setArmed(false)}>
        Cancel
      </button>
    </span>
  );
}
